/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable jsx-a11y/no-redundant-roles */
import React, { useState, useEffect } from 'react';
import "../assets/styles.css";
import NavBar from '../components/NavBar';
import categories from '../components/Categories';
import tokenCategories from '../components/ApeCategories';
import { Radio, RadioGroup, FormControlLabel, Checkbox, FormGroup, FormControl } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { ReactComponent as LargeThumbnails } from '../assets/img/large-thumbnails.svg';
import { ReactComponent as LargeThumbnailsActive } from '../assets/img/large-thumbnails-active.svg';
import { ReactComponent as SmallThumbnails } from '../assets/img/small-thumbnails.svg';
import { ReactComponent as SmallThumbnailsActive } from '../assets/img/small-thumbnails-active.svg';
import ThumbnailGrid from '../components/ThumbnailGrid';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import allDataImported from '../assets/all_data.min.json';

// IndexedDB setup
const DB_NAME = 'ExplorerDB';
const STORE_NAME = 'allDataStore';
const DB_VERSION = 1;

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      db.createObjectStore(STORE_NAME, { keyPath: 'id' });
    };
  });
};

const getDataFromDB = async (db) => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get('allData');
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result ? request.result.data : null);
  });
};

const saveDataToDB = async (db, data) => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put({ id: 'allData', data });
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
};

const CustomRadio = withStyles({
  root: {
    color: '#ffffff',
    '&$checked': {
      color: '#32efa5',
    },
  },
  checked: {},
})((props) => <Radio color="default" {...props} />);

const CustomCheckbox = withStyles({
  root: {
    color: '#ffffff',
    '&$checked': {
      color: '#32efa5',
    },
  },
  checked: {},
})((props) => <Checkbox color="default" {...props} />);

function Explorer() {
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const [activeTab, setActiveTab] = useState('TRAITS'); // Default to Traits
  const [paginationMode, setPaginationMode] = useState(() => 
    localStorage.getItem('paginationMode') || 'automatic'
  );
  const [thumbnailSize, setThumbnailSize] = useState(() => {
    const deviceKey = isSmallScreen ? 'mobileThumbnailSize' : 'desktopThumbnailSize';
    return localStorage.getItem(deviceKey) || (isSmallScreen ? 'small' : 'large');
  });
  const [categoryStates, setCategoryStates] = useState(
    categories.reduce((acc, category) => {
      acc[category.title] = { expanded: category.title === 'SORT' ? true : false, selected: '' }; // No default for Rarity sort
      return acc;
    }, {})
  );

  const [tokenCategoryStates, setTokenCategoryStates] = useState(
    tokenCategories.reduce((acc, category) => {
      if (category.subcategories) {
        acc[category.title] = {
          expanded: false,
          subcategories: category.subcategories.reduce((subAcc, sub) => {
            return { ...subAcc, [sub.title]: { expanded: false, selected: '' } };
          }, {}),
        };
      } else {
        acc[category.title] = { expanded: category.title === 'SORT' ? true : false, selected: category.title === 'SORT' ? 'ascending' : '' }; // Default to ascending token ID
      }
      return acc;
    }, {})
  );

  const [raritySortOrder, setRaritySortOrder] = useState('');
  const [rarityFilter, setRarityFilter] = useState('');
  const [tokenNumberSortOrder, setTokenNumberSortOrder] = useState('ascending'); // Default sort by token ID
  const [tokenNumberFilter, setTokenNumberFilter] = useState('');
  const [attributeCountFilter, setAttributeCountFilter] = useState([]);
  const [subcategoryFilter, setSubcategoryFilter] = useState([]);
  const [allData, setAllData] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const db = await openDB();
        const cachedData = await getDataFromDB(db);
        if (cachedData) {
          setAllData(cachedData);
        } else {
          setAllData(allDataImported);
          await saveDataToDB(db, allDataImported);
        }
        db.close();
      } catch (error) {
        console.error('Failed to load data from IndexedDB:', error);
        setAllData(allDataImported); // Fallback to imported data
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    localStorage.setItem('paginationMode', paginationMode);
  }, [paginationMode]);

  useEffect(() => {
    const deviceKey = isSmallScreen ? 'mobileThumbnailSize' : 'desktopThumbnailSize';
    const defaultSize = isSmallScreen ? 'small' : 'large';
    const savedSize = localStorage.getItem(deviceKey);
    setThumbnailSize(savedSize || defaultSize);
  }, [isSmallScreen]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleCategoryToggle = (title) => {
    if (title === 'SORT') return; // SORT is not collapsible
    setCategoryStates((prevStates) => ({
      ...prevStates,
      [title]: {
        ...prevStates[title],
        expanded: !prevStates[title].expanded,
      },
    }));
  };

  const handleTokenCategoryToggle = (title) => {
    if (title === 'SORT') return; // SORT is not collapsible
    setTokenCategoryStates((prevStates) => ({
      ...prevStates,
      [title]: {
        ...prevStates[title],
        expanded: !prevStates[title].expanded,
      },
    }));
  };

  const handleSubcategoryToggle = (title, subTitle) => {
    setTokenCategoryStates((prevStates) => ({
      ...prevStates,
      [title]: {
        ...prevStates[title],
        subcategories: {
          ...prevStates[title].subcategories,
          [subTitle]: {
            ...prevStates[title].subcategories[subTitle],
            expanded: !prevStates[title].subcategories[subTitle].expanded,
          },
        },
      },
    }));
  };

  const handleRadioChange = (title, value) => {
    setCategoryStates((prevStates) => ({
      ...prevStates,
      [title]: {
        ...prevStates[title],
        selected: value,
      },
    }));

    if (title === 'SORT') {
      setRaritySortOrder(value);
      // Deselect token ID sort options in Traits tab
      setTokenCategoryStates((prevStates) => ({
        ...prevStates,
        SORT: {
          ...prevStates.SORT,
          selected: '',
        },
      }));
      setTokenNumberSortOrder('');
    } else if (title === 'RARITY') {
      setRarityFilter(value);
    }
  };

  const handleTokenRadioChange = (title, value) => {
    setTokenCategoryStates((prevStates) => ({
      ...prevStates,
      [title]: {
        ...prevStates[title],
        selected: value,
      },
    }));

    if (title === 'SORT') {
      setTokenNumberSortOrder(value);
      // Deselect rarity sort options in Rarity tab
      setCategoryStates((prevStates) => ({
        ...prevStates,
        SORT: {
          ...prevStates.SORT,
          selected: '',
        },
      }));
      setRaritySortOrder('');
    } else {
      setSubcategoryFilter((prevFilters) => [
        ...prevFilters.filter((filter) => filter[0] !== title),
        [title, value],
      ]);
    }
  };

  const handleSubcategoryRadioChange = (title, subTitle, value) => {
    setTokenCategoryStates((prevStates) => ({
      ...prevStates,
      [title]: {
        ...prevStates[title],
        subcategories: {
          ...prevStates[title].subcategories,
          [subTitle]: {
            ...prevStates[title].subcategories[subTitle],
            selected: value,
          },
        },
      },
    }));

    setSubcategoryFilter((prevFilters) => [
      ...prevFilters.filter((filter) => filter[0] !== subTitle),
      [subTitle, value],
    ]);
  };

  const handleCheckboxChange = (title, value) => {
    setTokenCategoryStates((prevStates) => ({
      ...prevStates,
      [title]: {
        ...prevStates[title],
        selected: tokenCategoryStates[title].selected.includes(value)
          ? tokenCategoryStates[title].selected.filter((v) => v !== value)
          : [...tokenCategoryStates[title].selected, value],
      },
    }));

    if (title === 'ATTRIBUTE COUNT') {
      setAttributeCountFilter((prevFilters) =>
        tokenCategoryStates[title].selected.includes(value)
          ? prevFilters.filter((v) => v !== value)
          : [...prevFilters, value]
      );
    }
  };

  const handleClearFilters = () => {
    setCategoryStates(
      categories.reduce((acc, category) => {
        acc[category.title] = { expanded: category.title === 'SORT' ? true : false, selected: '' };
        return acc;
      }, {})
    );

    setTokenCategoryStates(
      tokenCategories.reduce((acc, category) => {
        if (category.subcategories) {
          acc[category.title] = {
            expanded: false,
            subcategories: category.subcategories.reduce((subAcc, subcategory) => {
              subAcc[subcategory.title] = { expanded: false, selected: '' };
              return subAcc;
            }, {})
          };
        } else {
          acc[category.title] = category.title === 'ATTRIBUTE COUNT' ? { expanded: false, selected: [] } : { expanded: category.title === 'SORT' ? true : false, selected: category.title === 'SORT' ? 'ascending' : '' };
        }
        return acc;
      }, {})
    );

    setRaritySortOrder('');
    setRarityFilter('');
    setSubcategoryFilter([]);
    setTokenNumberFilter('');
    setTokenNumberSortOrder('ascending'); // Reset to default
    setAttributeCountFilter([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasFilters = () => {
    let tokenCategoryHasFilters = false;

    const categoryHasFilters = Object.values(categoryStates).some((category) => category.selected !== '');

    Object.keys(tokenCategoryStates).forEach((categoryTitle) => {
      if (categoryTitle === 'ATTRIBUTE COUNT') {
        if (tokenCategoryStates[categoryTitle].selected.length > 0) {
          tokenCategoryHasFilters = true;
        }
      } else if (tokenCategoryStates[categoryTitle].subcategories) {
        Object.keys(tokenCategoryStates[categoryTitle].subcategories).forEach((subcategoryTitle) => {
          if (tokenCategoryStates[categoryTitle].subcategories[subcategoryTitle].selected !== '') {
            tokenCategoryHasFilters = true;
          }
        });
      } else {
        if (tokenCategoryStates[categoryTitle].selected !== '' && !(categoryTitle === 'SORT' && tokenCategoryStates[categoryTitle].selected === 'ascending')) {
          tokenCategoryHasFilters = true;
        }
      }
    });

    return categoryHasFilters || tokenCategoryHasFilters || raritySortOrder || rarityFilter || subcategoryFilter.length > 0 || tokenNumberFilter || attributeCountFilter.length > 0;
  };

  const handlePaginationChange = (value) => {
    setPaginationMode(value);
  };

  const handleThumbnailSizeChange = (size) => {
    setThumbnailSize(size);
    const deviceKey = isSmallScreen ? 'mobileThumbnailSize' : 'desktopThumbnailSize';
    localStorage.setItem(deviceKey, size);
  };

  useEffect(() => {
    console.log('Rarity Sort Order:', raritySortOrder);
    console.log('Token Number Sort Order:', tokenNumberSortOrder);
  }, [raritySortOrder, tokenNumberSortOrder]);

  const renderPaginationOptions = () => (
    <div className="category">
      <div className="category-title">PAGINATION</div>
      <div className="category-content">
        <RadioGroup
          value={paginationMode}
          onChange={(event) => handlePaginationChange(event.target.value)}
        >
          <FormControlLabel
            value="automatic"
            control={<CustomRadio />}
            label={<span className="radio-label">Automatic</span>}
          />
          <FormControlLabel
            value="manual"
            control={<CustomRadio />}
            label={<span className="radio-label">Manual</span>}
          />
        </RadioGroup>
      </div>
    </div>
  );

  return (
    <section className='hero'>
      <NavBar />
      <section className={`explorer-container ${isSmallScreen ? 'mobile' : ''}`}>
        <aside className="left-panel">
          <div className="tabs">
            <button
              className={`tab-button ${activeTab === 'TRAITS' ? 'active' : ''}`}
              onClick={() => handleTabClick('TRAITS')}
            >
              TRAITS
            </button>
            <button
              className={`tab-button ${activeTab === 'RARITY' ? 'active' : ''}`}
              onClick={() => handleTabClick('RARITY')}
            >
              RARITY
            </button>
          </div>
          {activeTab === 'TRAITS' && (
            <div className="tokens-content">
              <div className="thumbnail-buttons">
                {thumbnailSize === 'large' ? (
                  <LargeThumbnailsActive
                    className="thumbnail-icon active"
                    onClick={() => handleThumbnailSizeChange('large')}
                  />
                ) : (
                  <LargeThumbnails
                    className="thumbnail-icon"
                    onClick={() => handleThumbnailSizeChange('large')}
                  />
                )}
                {thumbnailSize === 'small' ? (
                  <SmallThumbnailsActive
                    className="thumbnail-icon active"
                    onClick={() => handleThumbnailSizeChange('small')}
                  />
                ) : (
                  <SmallThumbnails
                    className="thumbnail-icon"
                    onClick={() => handleThumbnailSizeChange('small')}
                  />
                )}
              </div>
              <div className="search-container">
                <span className="search-title">SEARCH</span>
                <input
                  type="text"
                  className="search-field"
                  placeholder="Token Number"
                  value={tokenNumberFilter}
                  onChange={(e) => setTokenNumberFilter(e.target.value)}
                />
              </div>
              <div className="tab-content">
                {renderPaginationOptions()}
                {tokenCategories.map((category) => (
                  <div key={category.title} className="category">
                    <div
                      className="category-title"
                      onClick={() => handleTokenCategoryToggle(category.title)}
                    >
                      {category.title.charAt(0).toUpperCase() + category.title.slice(1)}
                      {category.title !== 'SORT' && (
                        <span className="toggle-icon">
                          {tokenCategoryStates[category.title].expanded ? '−' : '+'}
                        </span>
                      )}
                    </div>
                    {(tokenCategoryStates[category.title].expanded || category.title === 'SORT') && !category.subcategories && (
                      <div className="category-content">
                        {category.title === 'ATTRIBUTE COUNT' ? (
                          <FormControl component="fieldset">
                            <FormGroup>
                              {category.options.map((option) => (
                                <FormControlLabel
                                  key={option.value}
                                  control={
                                    <CustomCheckbox
                                      checked={tokenCategoryStates[category.title].selected.includes(option.value)}
                                      onChange={() => handleCheckboxChange(category.title, option.value)}
                                    />
                                  }
                                  label={<span className="checkbox-label">{option.label}</span>}
                                />
                              ))}
                            </FormGroup>
                          </FormControl>
                        ) : (
                          <RadioGroup
                            value={tokenCategoryStates[category.title].selected}
                            onChange={(event) => {
                              handleTokenRadioChange(category.title, event.target.value);
                              console.log(`${category.title} selected:`, event.target.value);
                            }}
                          >
                            {category.options.map((option) => (
                              <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<CustomRadio />}
                                label={<span className="radio-label">{option.label}</span>}
                              />
                            ))}
                          </RadioGroup>
                        )}
                      </div>
                    )}
                    {(tokenCategoryStates[category.title].expanded || category.title === 'SORT') && category.subcategories && (
                      <div className="category-content">
                        {category.subcategories.map((subcategory) => (
                          <div key={subcategory.title} className="subcategory">
                            <div
                              className="subcategory-title"
                              onClick={() => handleSubcategoryToggle(category.title, subcategory.title)}
                            >
                              {subcategory.title}
                              <span className="toggle-icon">
                                {tokenCategoryStates[category.title].subcategories[subcategory.title].expanded ? '−' : '+'}
                              </span>
                            </div>
                            {tokenCategoryStates[category.title].subcategories[subcategory.title].expanded && (
                              <div className="subcategory-content">
                                <RadioGroup
                                  value={tokenCategoryStates[category.title].subcategories[subcategory.title].selected}
                                  onChange={(event) => {
                                    handleSubcategoryRadioChange(category.title, subcategory.title, event.target.value);
                                    console.log(`${subcategory.title} selected:`, event.target.value);
                                  }}
                                >
                                  {subcategory.options.map((option) => (
                                    <FormControlLabel
                                      key={option.value}
                                      value={option.value}
                                      control={<CustomRadio />}
                                      label={<span className="radio-label">{option.label}</span>}
                                    />
                                  ))}
                                </RadioGroup>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {activeTab === 'RARITY' && (
            <div>
              <div className="thumbnail-buttons">
                {thumbnailSize === 'large' ? (
                  <LargeThumbnailsActive
                    className="thumbnail-icon active"
                    onClick={() => handleThumbnailSizeChange('large')}
                  />
                ) : (
                  <LargeThumbnails
                    className="thumbnail-icon"
                    onClick={() => handleThumbnailSizeChange('large')}
                  />
                )}
                {thumbnailSize === 'small' ? (
                  <SmallThumbnailsActive
                    className="thumbnail-icon active"
                    onClick={() => handleThumbnailSizeChange('small')}
                  />
                ) : (
                  <SmallThumbnails
                    className="thumbnail-icon"
                    onClick={() => handleThumbnailSizeChange('small')}
                  />
                )}
              </div>
              <div className="tab-content">
                {renderPaginationOptions()}
                {categories.map((category) => (
                  <div key={category.title} className="category">
                    <div
                      className="category-title"
                      onClick={() => handleCategoryToggle(category.title)}
                    >
                      {category.title}
                      {category.title !== 'SORT' && (
                        <span className="toggle-icon">
                          {categoryStates[category.title].expanded ? '−' : '+'}
                        </span>
                      )}
                    </div>
                    {(categoryStates[category.title].expanded || category.title === 'SORT') && (
                      <div className="category-content">
                        <RadioGroup
                          value={categoryStates[category.title].selected}
                          onChange={(event) => {
                            handleRadioChange(category.title, event.target.value);
                            console.log(`${category.title} selected:`, event.target.value);
                          }}
                        >
                          {category.options.map((option) => (
                            <FormControlLabel
                              key={option.value}
                              value={option.value}
                              control={<CustomRadio />}
                              label={<span className="radio-label">{option.label}</span>}
                            />
                          ))}
                        </RadioGroup>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {hasFilters() && (
            <button className="clear-filters-button" onClick={handleClearFilters}>
              CLEAR FILTERS
            </button>
          )}
        </aside>
        <main className="content">
          <ThumbnailGrid
            thumbnailSize={thumbnailSize}
            categoryStates={categoryStates}
            tokenCategoryStates={tokenCategoryStates}
            raritySortOrder={raritySortOrder}
            rarityFilter={rarityFilter}
            subcategoryFilter={subcategoryFilter}
            tokenNumberFilter={tokenNumberFilter}
            tokenNumberSortOrder={tokenNumberSortOrder}
            attributeCountFilter={attributeCountFilter}
            allData={allData}
            paginationMode={paginationMode}
          />
        </main>
      </section>
    </section>
  );
}

export default Explorer;
